
import React, { useState, useEffect } from "react";
import { Button, Select, Input, Spin, message } from "antd";
import { createProject, updateException, getAllProjects } from "../api";
import Hide from "../../components/Hide";
import { ExceptionStatus } from "../ExceptionDrawer";

const { Option } = Select;

const ProjectComponent = ({ exception, onClose, onResolved }) => {
    const [projectName, setProjectName] = useState(null);
    const [showSelect, setShowSelect] = useState(false);
    const [projects, setProjects] = useState([]);
    const [selectedAliasId, setSelectedAliasId] = useState(null);
    const [code, setCode] = useState("");
    const [wb_id, setWb_Id] = useState("");
    const [showInput, setShowInput] = useState(false);
    const [loading, setLoading] = useState(false); 

    useEffect(() => {
        const fetchProjects = async () => {
            setLoading(true); 
            try {
                const allProjects = (await getAllProjects())?.data;
                setProjects(allProjects);
            } catch (error) {
                console.error("Failed to fetch projects", error);
            } finally {
                setLoading(false); 
            }
        };

        const extractProjectName = (message) => {
            const regex = /Project\s'([^']+)'/;
            const match = message.match(regex);
            setProjectName(match ? match[1] : null);
        };

        if (exception && exception.description) {
            extractProjectName(exception.description);
        }

        fetchProjects();
    }, [exception]);

    const handleMarkAsNew = async () => {
        if (!code) {
            console.warn("Please enter a code.");
            return;
        }

        if (projectName) {
            setLoading(true); 
            try {
                if (exception.status === 'pending') {
                    await createProject({ 
                        name: projectName, 
                        code, 
                        project_status_id: 1, 
                        wb_project_id: wb_id 
                    });
                    await updateException(exception.id);
                    console.log(`Project "${projectName}" created successfully.`);
                    message.success('Project created');
                    try { if (typeof onResolved === 'function') await onResolved(); } catch {}
                    onClose();
                } else {
                    console.warn("Exception already resolved.");
                }
            } catch (error) {
                console.error(`Failed to create project "${projectName}".`, error);
                message.error('Failed to create project');
            } finally {
                setLoading(false); 
            }
        } else {
            console.warn("No project name found.");
        }
    };

    const handleMarkAsAliasFor = () => {
        setShowSelect(true);
        setShowInput(false);
    };

    const handleMarkAsNewClick = () => {
        setShowInput(true);
        setShowSelect(false);
    };

    const handleProjectSelect = (value) => {
        setSelectedAliasId(value ?? null);
    };

    const handleSaveAlias = async () => {
        if (!selectedAliasId) return;
        const project = projects.find(p => p.id === selectedAliasId);
        setLoading(true);
        try {
            if (exception.status === 'pending') {
                await createProject({ 
                    name: projectName, 
                    alias_for: project.id, 
                    code: project.code, 
                    project_status_id: project.project_status_id || 1, 
                    wb_project_id: project.wb_project_id 
                });
                await updateException(exception.id);
                console.log(`Project "${projectName}" set as alias for project with ID: ${selectedAliasId}`);
                message.success('Alias saved');
                try { if (typeof onResolved === 'function') await onResolved(); } catch {}
                onClose();
            } else {
                console.warn("Exception already resolved.");
            }
        } catch (error) {
            console.error(`Failed to update alias for project with ID: ${selectedAliasId}`, error);
            message.error('Failed to save alias');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div style={{ marginTop: 20 }}>
            <Hide condition={exception.status === ExceptionStatus.RESOLVED}>
                <Spin spinning={loading} tip="Processing...">
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                        <Button 
                            type="primary" 
                            onClick={handleMarkAsNewClick}
                            disabled={loading}
                        >
                            Mark as New
                        </Button>
                        <Button 
                            type="default" 
                            onClick={handleMarkAsAliasFor}
                            disabled={loading}
                        >
                            Mark as Alias For
                        </Button>
                    </div>

                    {showInput && (
                        <div style={{ marginTop: 8 }}>
                            <Input 
                                placeholder="Enter project code" 
                                value={code} 
                                onChange={(e) => setCode(e.target.value)} 
                                style={{ marginBottom: 10 }}
                                disabled={loading}
                            />
                            <Input 
                                placeholder="Enter world bank project id" 
                                value={wb_id} 
                                onChange={(e) => setWb_Id(e.target.value)} 
                                style={{ marginBottom: 10 }}
                                disabled={loading}
                            />
                            <Button 
                                type="primary" 
                                onClick={handleMarkAsNew}
                                disabled={loading || !code}
                                loading={loading}
                            >
                                Submit
                            </Button>
                        </div>
                    )}

                    {showSelect && (
                        <div style={{ marginTop: 8, display: 'flex', gap: 8, alignItems: 'center' }}>
                            <div style={{ fontSize: 12, color: '#666' }}>
                                {projectName ? `Select an existing project to alias "${projectName}" to.` : 'Select an existing project to alias.'}
                            </div>
                            <Select
                                style={{ minWidth: 220 }}
                                placeholder="Select a project"
                                onChange={handleProjectSelect}
                                disabled={loading}
                                value={selectedAliasId ?? undefined}
                            >
                                {projects?.map((project) => (
                                    <Option key={project.id} value={project.id}>
                                        {project.name}
                                    </Option>
                                ))}
                            </Select>
                            <Button
                                type="primary"
                                onClick={handleSaveAlias}
                                disabled={loading || !selectedAliasId}
                                loading={loading}
                            >
                                Save
                            </Button>
                        </div>
                    )}
                </Spin>
            </Hide>
        </div>
    );
};

export default ProjectComponent;
